import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createWidget } from 'actions/editor';
import {
  fetchPaletteFolders, fetchComponentsForPalette, fetchComponentsForPaletteFolder
} from 'actions/palette';

import { Tab } from 'react-bootstrap';
import _ from "lodash";
import cx from 'classnames';

import { Widgets } from '@paperflite-marketing/cleverstory-commons';

import { PaletteContext } from '.';
import { getPositionForNewWidget } from 'components/Editor/helper/layoutManager';
import InfiniteScroll from "components/InfiniteScroll";
import { getStyles } from 'components/Editor/widgets/button';

import { BUTTON } from 'constants/widgetTypes';

import styles from './palette.module.scss';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_BRANDKIT_CREATED, MIXPANEL_WIDGET_ADDED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import StoryboardContext from 'components/Storyboard/context';

export const ButtonPalette = ({ editorSession, defaultParent }) => {
  const paletteContext = React.useContext(PaletteContext);

  const { palettes = [] } = paletteContext;

  const paletteData = palettes.find(item => item.type === 'BUTTON');
  const defaultId = _.get(paletteData, 'defaultId', null);
  const brandkitId = _.get(paletteData, 'brandkitId', null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const showDefault = _.get(paletteData, 'showDefault', false) || !brandkitEnabled;

  return (
    <Tab.Pane eventKey="BUTTON" className={cx(styles.tabPane, styles.buttonPalette)}>
      {brandkitEnabled &&
        <ButtonPaletteView 
          editorSession={editorSession}
          defaultParent={defaultParent}
          paletteId={brandkitId}
          type={'brandkit'}
        />
      }
      
      {showDefault &&
        <ButtonPaletteView
          editorSession={editorSession}
          defaultParent={defaultParent}
          paletteId={defaultId}
          type={'default'}
        />
      }
    </Tab.Pane>
  );
}

export const ButtonPaletteView = ({ editorSession, defaultParent, paletteId, type }) => {
  const dispatch = useDispatch();

  const paletteContext = React.useContext(PaletteContext);
  const storyboardContext = React.useContext(StoryboardContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const components = useSelector(state => { return _.get(state, `palette.[${type}][${paletteId}].items`) || [] })
  const loading = useSelector(state => { return _.get(state, `palette.[${type}][${paletteId}].loading`) || false })
  const fetched = useSelector(state => { return _.get(state, `palette.[${type}][${paletteId}].fetched`) || false })
  const pagination = useSelector(state => { return _.get(state, `palette.[${type}][${paletteId}].pagination`) || {} })
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  useEffect(() => {
    if (paletteId && !fetched) {
      handleComponentsForPalette(0);
    }
  }, [paletteId]);

  const handleComponentsForPalette = (page) => {
    var request = {
      sort: 'order',
      order: 'ASC'
    }
    dispatch(fetchComponentsForPalette(paletteId, type, request, page, 50));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleComponentsForPalette(nextPage);
  }

  const handleCreateButton = (data, type) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 260, height: 40 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 260, height: 40 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: BUTTON,
            name: data.name || "Button",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  }
                },
              },
            },
            content: data.content,
          },
        ],
        defaultParent
      )
    );
      var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
      mixpanelPayload = {
        ...mixpanelPayload,
        widget_type: 'Buttons',
        brandkit_widget: type === 'brandkit'
      }
      dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))

    paletteContext.close();
  }

  if(components.length === 0) {
    return null;
  }

  return (
    <div className={styles.defaultWidgets}>
      <div className={styles.paletteTitle}>
        <h3 className={styles.label}>{type === 'brandkit' ? 'BRAND BUTTONS' : 'CLEVERSTORY'}</h3>
        <hr className={styles.labelHighliter} />
      </div>
      <InfiniteScroll
        className={cx(styles.scrollable)}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={loadMore}
      >
        <div className={styles.grid}>
          {components.map(component => {
            const widgets = _.get(component, 'content.widgets', []);
            if (!widgets) {
              return null;
            }

            const widget = widgets[0];

            var buttonStyles = getStyles(widget.properties, 'desktop', 'regular');
            buttonStyles = {
              ...buttonStyles,
              width: _.get(widget, 'properties.desktop.layout.width', 'auto'),
              height: _.get(widget, 'properties.desktop.layout.height', 'auto')
            }

            return (
              <div className={cx(styles.widget, {[styles.defaultWidget]: type === 'default'})} key={widget.id} id={component.id} onClick={() => {
                handleCreateButton(widget, type);
              }}>
                <Widgets.Widget widgetProps={widget} breakpoint='desktop' />
              </div>
            )
          })}
        </div>
      </InfiniteScroll>
    </div>
  )
}